import { supabase } from './supabaseClient';
import { API_ENDPOINTS, API_BASE_URL } from './config/apiConfig';

// Create a new booking request
export async function createBookingRequest(data) {
  let finalPrice = data.total_price;
  let discountAmount = 0;
  let discountType = null;
  let couponCode = null;

  // Check for Level 100 permanent discount
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const { data: student } = await supabase
      .from('students')
      .select('has_level_100_discount')
      .eq('uuid_id', user.id)
      .single();

    if (student?.has_level_100_discount) {
      // Apply 20% permanent discount
      discountAmount = finalPrice * 0.20;
      finalPrice = finalPrice - discountAmount;
      discountType = 'level_100_permanent';
    }
    // Only apply coupon if no level 100 discount
    else if (data.coupon_code) {
      try {
        // Validate and apply coupon via backend API
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        const response = await fetch(`${API_BASE_URL}/booking/validate-coupon`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            coupon_code: data.coupon_code,
            booking_total: data.total_price
          })
        });

        const result = await response.json();
        if (result.valid) {
          discountAmount = result.discount_amount;
          finalPrice = result.new_total;
          discountType = 'coupon';
          couponCode = data.coupon_code;
        }
      } catch (error) {
        console.error('Coupon validation error:', error);
      }
    }
  }

  // Create booking with discount data
  const bookingData = {
    ...data,
    original_price: data.total_price,
    total_price: finalPrice,
    discount_amount: discountAmount,
    discount_type: discountType,
    coupon_code: couponCode
  };

  const { data: result, error } = await supabase
    .from('booking_requests')
    .insert([bookingData])
    .select()
    .single();
  
  // If insert succeeded, send notification
  if (result && !error) {
    try {
      const emailPayload = {
        teacherEmail: result.teacher_email || '',
        teacherName: result.teacher_name || 'Teacher',  // Default for null names
        studentEmail: result.student_email || '',
        studentName: result.student_name || 'Student',
        requestType: result.request_type || 'individual',
        date: result.date || '',
        timeFrom: result.time_from || '',
        timeTo: result.time_to || '',
        totalPrice: result.total_price || 0,
        // Always include optional fields with defaults
        place: result.place || '',
        subject: result.subject || '',
        numStudents: result.num_students || 1,
        participantEmails: result.participant_emails || [],
        // Location details for face-to-face sessions
        lessonFormat: result.request_type || '',
        locationFormattedAddress: result.location_formatted_address || '',
        locationLatitude: result.location_latitude || null,
        locationLongitude: result.location_longitude || null
      };

      console.log('📤 Sending email notification with payload:', emailPayload);

      const emailResponse = await fetch(API_ENDPOINTS.SEND_BOOKING_REQUEST, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload)
      });

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json();
        console.error('❌ Email notification failed:', errorData);
        console.error('Status:', emailResponse.status);
        if (errorData.details && errorData.details.length > 0) {
          console.error('Validation errors:');
          errorData.details.forEach(err => {
            console.error(`  - ${err.field}: ${err.message}`);
          });
        }
      } else {
        console.log('✅ Email notification sent successfully');
      }
    } catch (emailError) {
      console.error('Error sending request notification email:', emailError);
    }

    // If booking created successfully and coupon was used, mark it as used
    if (result && couponCode) {
      try {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        await fetch(`${API_BASE_URL}/wallet/use-coupon`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            coupon_code: couponCode,
            booking_id: result.id
          })
        });
      } catch (couponError) {
        console.error('Error marking coupon as used:', couponError);
      }
    }

    return { result, error };
  }

  // Handle unique constraint violation: use existing booking and still send email
  // Supabase/Postgres unique violation code is 23505; client often surfaces 409 status
  const isUniqueViolation = (error && (
    error.code === '23505' ||
    String(error.message || '').toLowerCase().includes('duplicate key value') ||
    String(error.details || '').toLowerCase().includes('already exists')
  ));

  if (isUniqueViolation) {
    // Return a user-friendly error for duplicate bookings
    return {
      result: null,
      error: {
        message: 'You already have a pending booking request for this teacher at this time. Please check your booking requests.',
        code: 'DUPLICATE_BOOKING'
      }
    };
  }

  // Return original error for other cases
  return { result: null, error };
}

// =============================
// Ratings helpers
// =============================
export async function submitRating({ bookingId, studentId, studentEmail, teacherId, teacherEmail, rating, comment, tags = [] }) {
  // Validate required parameters
  if (!studentEmail) {
    console.error('submitRating: studentEmail is required');
    return { data: null, error: new Error('Student email is required') };
  }
  if (!teacherEmail) {
    console.error('submitRating: teacherEmail is required');
    return { data: null, error: new Error('Teacher email is required') };
  }

  const { data, error } = await supabase
    .from('ratings')
    .insert([{ booking_id: bookingId, student_id: studentId || null, student_email: studentEmail, teacher_id: teacherId || null, teacher_email: teacherEmail, rating, comment, tags }])
    .select()
    .single();

  // Award XP for rating and session completion (fire and forget - don't block on XP)
  if (data && !error) {
    const ratingId = data.id;

    // Get auth token for API calls
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (token) {
      // Award 10 XP for rating (async, don't await)
      fetch(API_ENDPOINTS.XP_RATING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ratingId,
          studentId,
          studentEmail,
          bookingId
        })
      }).catch(err => console.error('Rating XP error:', err));

      // Award 50 XP for session completion (async, don't await)
      fetch(API_ENDPOINTS.XP_SESSION_COMPLETE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bookingId,
          studentId,
          studentEmail
        })
      }).catch(err => console.error('Session complete XP error:', err));
    }
  }

  return { data, error };
}

export async function getTeacherRatingSummary(teacherEmail) {
  // Prefer the view if available, fallback to aggregate
  let { data, error } = await supabase
    .from('teacher_ratings_summary')
    .select('teacher_email, avg_rating, ratings_count')
    .eq('teacher_email', teacherEmail)
    .maybeSingle();
  if (!error && data) return { data, error: null };
  // Fallback aggregate
  const agg = await supabase
    .from('ratings')
    .select('rating', { count: 'exact' })
    .eq('teacher_email', teacherEmail);
  if (agg.error) return { data: null, error: agg.error };
  const ratings = agg.data || [];
  const count = agg.count || 0;
  const avg = ratings.length ? (ratings.reduce((s, r) => s + (r.rating || 0), 0) / ratings.length) : 0;
  return { data: { teacher_email: teacherEmail, ratings_count: count, avg_rating: Math.round(avg * 100) / 100 }, error: null };
}

/**
 * Fetch ratings for multiple teachers efficiently (single query)
 * @param {string[]} teacherEmails - Array of teacher email addresses
 * @returns {Promise<{data: Object, error: any}>} Map of email → {avg_rating, ratings_count}
 */
export async function getMultipleTeacherRatings(teacherEmails) {
  if (!teacherEmails || teacherEmails.length === 0) {
    return { data: {}, error: null };
  }

  // Try teacher_ratings_summary view first (preferred)
  let { data, error } = await supabase
    .from('teacher_ratings_summary')
    .select('teacher_email, avg_rating, ratings_count')
    .in('teacher_email', teacherEmails);

  if (error) {
    console.error('Error fetching ratings summary:', error);
    return { data: {}, error };
  }

  // Convert array to map for O(1) lookup
  const ratingsMap = {};
  if (data) {
    data.forEach(rating => {
      ratingsMap[rating.teacher_email] = {
        avg_rating: rating.avg_rating || 0,
        ratings_count: rating.ratings_count || 0
      };
    });
  }

  return { data: ratingsMap, error: null };
}

/**
 * Get rating breakdown by star level for a teacher
 * @param {string} teacherEmail - Teacher's email address
 * @returns {Promise<{data: Object, error: any}>} Object with counts for each star level (1-5)
 */
export async function getTeacherRatingBreakdown(teacherEmail) {
  console.log('[getTeacherRatingBreakdown] Fetching for email:', teacherEmail);

  const { data, error } = await supabase
    .from('ratings')
    .select('rating')
    .eq('teacher_email', teacherEmail);

  console.log('[getTeacherRatingBreakdown] Result:', { data, error, count: data?.length });

  if (error) {
    console.error('Error fetching rating breakdown:', error);
    return { data: null, error };
  }

  // Count ratings by star level
  const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  if (data) {
    data.forEach(r => {
      const rounded = Math.round(r.rating);
      if (rounded >= 1 && rounded <= 5) {
        breakdown[rounded]++;
      }
    });
  }

  console.log('[getTeacherRatingBreakdown] Breakdown:', breakdown);
  return { data: breakdown, error: null };
}

export async function getTeacherLatestFeedback(teacherEmail, limit = 10) {
  console.log('[getTeacherLatestFeedback] Fetching for email:', teacherEmail);

  const { data, error } = await supabase
    .from('ratings')
    .select('rating, comment, tags, created_at')
    .eq('teacher_email', teacherEmail)
    .order('created_at', { ascending: false })
    .limit(limit);

  console.log('[getTeacherLatestFeedback] Result:', { data, error, count: data?.length });
  return { data, error };
}

// Get all booking requests for a student (optionally filter by teacher)
// Also includes group bookings where the student is a participant
export async function getStudentBookingRequests(student_email, teacher_email = null) {
  // Query for own bookings OR group bookings where student is a participant
  let query = supabase
    .from('booking_requests')
    .select('id, teacher_email, teacher_name, student_email, student_name, date, time_from, time_to, total_price, status, payment_status, place, subject, request_type, num_students, participant_emails, created_at, meeting_link, meeting_room_id, meeting_type')
    .or(`student_email.eq.${student_email},participant_emails.cs.{"${student_email}"}`)
    .order('created_at', { ascending: false });
  if (teacher_email) query = query.eq('teacher_email', teacher_email);
  const { data, error } = await query;
  return { data, error };
}

// Get all booking requests for a teacher
export async function getTeacherBookingRequests(teacher_email) {
  const { data, error } = await supabase
    .from('booking_requests')
    .select('id, teacher_email, teacher_name, student_email, student_name, date, time_from, time_to, total_price, status, payment_status, place, subject, request_type, num_students, participant_emails, created_at, meeting_link, meeting_room_id, meeting_type')
    .eq('teacher_email', teacher_email)
    .order('created_at', { ascending: false });
  return { data, error };
}

export async function requestReschedule(bookingId, { studentId, newDate, newTimeFrom, newTimeTo, reason }) {
  // Get auth token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const requestData = { studentId, newDate, newTimeFrom, newTimeTo, reason };
  console.log('Reschedule request data:', requestData);

  const response = await fetch(`${API_BASE_URL}/api/booking-requests/${bookingId}/reschedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(requestData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('Reschedule error response:', error);
    if (error.details && error.details.length > 0) {
      console.error('Validation errors:', error.details);
      // Build detailed error message
      const errorMessages = error.details.map(d => `${d.field}: ${d.message}`).join(', ');
      throw new Error(`Validation failed: ${errorMessages}`);
    }
    throw new Error(error.error || error.message || 'Failed to request reschedule');
  }
  return response.json();
}

export async function respondToReschedule(bookingId, { decision, teacherId }) {
  // Get auth token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  // Convert decision format: 'accepted' -> 'accept', 'declined' -> 'decline'
  const action = decision === 'accepted' ? 'accept' : 'decline';

  const response = await fetch(`${API_BASE_URL}/api/booking-requests/${bookingId}/reschedule/decision`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ action, teacherId })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update reschedule');
  }
  return response.json();
}

// Generate Zoom meeting link using Zoom API
// This creates a real Zoom meeting link that will work properly
async function generateZoomMeetingLink(bookingId, teacherName, studentName) {
  try {
    // Get the current session token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    
    if (!token) {
      console.error('No auth token available for Zoom meeting creation');
      return createFallbackZoomLink(bookingId);
    }
    
    // Call the backend API to create a Zoom meeting
    const response = await fetch(`${API_BASE_URL}/api/create-zoom-meeting`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        bookingId,
        teacherName,
        studentName,
        // Add other booking details as needed
      }),
    });

    if (response.ok) {
      const result = await response.json();
      
      // Zoom API returns join_url in various formats:
      // - https://us05web.zoom.us/j/MEETING_ID?pwd=PASSWORD
      // - https://zoom.us/j/MEETING_ID
      // - https://*.zoom.us/j/MEETING_ID
      // All of these are valid meeting room links
      const isValidZoomLink = result.meetingLink && 
        (result.meetingLink.includes('zoom.us/j/') || result.meetingLink.includes('zoom.us/s/'));
      
      if (isValidZoomLink) {
        console.log('✅ Real Zoom meeting link created:', result.meetingLink);
      } else if (result.meetingLink === 'https://zoom.us/' || result.meetingLink === 'https://zoom.us') {
        console.warn('⚠️ Fallback Zoom link returned - check backend logs and .env credentials');
      } else {
        console.warn('⚠️ Unexpected Zoom link format:', result.meetingLink);
      }
      
      return {
        meetingLink: result.meetingLink,
        roomId: result.roomId,
        meetingType: result.meetingType || 'zoom'
      };
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to create Zoom meeting:', errorText);
      return createFallbackZoomLink(bookingId);
    }
  } catch (error) {
    console.error('Error creating Zoom meeting:', error);
    return createFallbackZoomLink(bookingId);
  }
}

// Fallback method for when Zoom API is not available
function createFallbackZoomLink(bookingId) {
  const timestamp = Date.now().toString(36);
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const meetingId = `taealam-zoom-${timestamp}-${randomSuffix}`;
  // No instant public Zoom link—direct user to Zoom and instruct to share link
  const meetingLink = `https://zoom.us/`; 
  
  return {
    meetingLink,
    roomId: meetingId,
    meetingType: 'zoom_manual',
    instructions: `Please create a Zoom meeting and share the link with your student. Placeholder ID: ${meetingId}`
  };
}

// Update booking request status (accept/decline)
export async function updateBookingRequestStatus(id, status) {
  // If accepting, generate meeting link ONLY if one doesn't exist
  let updateData = { status, updated_at: new Date().toISOString() };
  let currentUser = null;

  if (status === 'accepted') {
    // Get the currently logged-in user (teacher who is accepting)
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('Error getting current user:', authError);
      return { data: null, error: 'Authentication error - please sign in again' };
    }
    currentUser = user;

    // Note: teacher_id and teacher_uuid are already set when the booking was created
    // No need to update them here - they should remain unchanged
    console.log('✅ Teacher authenticated for booking:', currentUser.id);

    // REMOVED: Meeting link generation now happens ONLY after payment confirmation
    // The payment webhook will generate meeting links for online sessions only
    // This ensures: 1) Students pay before getting meeting access, 2) Face-to-face sessions don't get unnecessary Zoom links
    console.log('📌 Booking accepted - meeting link will be generated after payment (if online session)');
  }
  
  const { data, error } = await supabase
    .from('booking_requests')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();
  if (error) return { data, error };
  // If accepted, create conversation
  if (status === 'accepted' && data && currentUser) {
    // Use the student ID from booking and current teacher's Auth ID
    const studentId = data.student_id;
    const teacherId = currentUser.id; // Use current teacher's Auth ID (already fetched earlier)
    
    if (!studentId || !teacherId) {
      console.error('Missing studentId or teacherId for conversation check');
      return { data, error: 'Missing user IDs' };
    }
    try {
      // Check if conversation already exists (both orderings)
      const { data: existing, error: existingError } = await supabase
        .from('conversations')
        .select('id, user1_id, user2_id')
        .or(`and(user1_id.eq.${studentId},user2_id.eq.${teacherId}),and(user1_id.eq.${teacherId},user2_id.eq.${studentId})`)
        .limit(1);
      
      if (existingError) {
        console.error('Error checking existing conversations:', existingError);
      }
      
      if (!existing || existing.length === 0) {
        // Create new conversation
        const conversationData = { user1_id: studentId, user2_id: teacherId };
        
        const { error: insertError } = await supabase
          .from('conversations')
          .insert([conversationData])
          .select();
        
        if (insertError) {
          console.error('Error creating conversation:', insertError);
        }
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }

    // Award XP for new subject booking (fire and forget)
    if (data.student_id && data.student_email && data.subject) {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (token) {
        fetch(API_ENDPOINTS.XP_NEW_SUBJECT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            bookingId: data.id,
            studentId: data.student_id,
            studentEmail: data.student_email,
            subject: data.subject
          })
        }).catch(err => console.error('New subject XP error:', err));
      }
    }
  }
  // Send email notification for both accepted and rejected
  try {
    const emailResponse = await fetch(API_ENDPOINTS.SEND_BOOKING_STATUS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        bookingId: data.id,
        teacherEmail: data.teacher_email,
        teacherName: data.teacher_name,
        studentEmail: data.student_email,
        studentName: data.student_name,
        requestType: data.request_type,
        date: data.date,
        timeFrom: data.time_from,
        timeTo: data.time_to,
        place: data.place,
        numStudents: data.num_students,
        totalPrice: data.total_price,
        status,
        meetingLink: data.meeting_link,
        meetingType: data.meeting_type,
        participantEmails: data.participant_emails
      })
    });
    
    if (!emailResponse.ok) {
      console.error('Email sending failed:', emailResponse.status, emailResponse.statusText);
    }
  } catch (emailError) {
    console.error('Error sending email:', emailError);
  }
  
  return { data, error: null };
}

// Delete a booking request
export async function deleteBookingRequest(id) {
  const { error } = await supabase
    .from('booking_requests')
    .delete()
    .eq('id', id);
  return { error };
}

// Create a conversation between two users (student and teacher)
export async function createConversation(user1_id, user2_id) {
  // Check if conversation already exists
  const { data: existing } = await supabase
    .from('conversations')
    .select('id, user1_id, user2_id, created_at')
    .or(`user1_id.eq.${user1_id},user2_id.eq.${user1_id}`)
    .or(`user1_id.eq.${user2_id},user2_id.eq.${user2_id}`)
    .limit(1);
  if (existing && existing.length > 0) return { data: existing[0], alreadyExists: true };
  // Create new conversation
  const { data, error } = await supabase
    .from('conversations')
    .insert([{ user1_id, user2_id }])
    .select('id, user1_id, user2_id, created_at')
    .single();
  return { data, error };
} 